/**
 * Every run on one timeline: each Scout run with what omnium did with it, so
 * "Scout passed but omnium did not read it" stands out — and one run opened
 * with its log and rows.
 */

import { useState, type ReactNode } from "react";
import {
  Check,
  ChevronDown,
  ChevronRight,
  CircleAlert,
  FlaskConical,
  Info,
  LoaderCircle,
  Minus,
  TriangleAlert,
  X,
} from "lucide-react";
import { Link, useSearchParams } from "react-router-dom";

import { RunRows } from "@/components/integrations/RunRows";
import {
  Banner,
  Button,
  Card,
  Column,
  Crumbs,
  Empty,
  Field,
  FieldRow,
  Note,
  OutcomeBar,
  Panel,
  Section,
  Select,
  SkeletonRows,
  Tile,
  Tiles,
  Timeline,
  TimelineItem,
  TimelineLine,
  TimelineMain,
  TimelineOne,
  TimelineStack,
  TimelineSub,
  TonePill,
} from "@/components/kit";
import { getRun, getRunLog, listScoutRuns, listVersions, replayRun, type IntegrationDetail, type Run } from "@/lib/integrations/api";
import { lineUpRuns, type TimelineEntry } from "@/lib/integrations/timeline";
import {
  ago,
  extraCounts,
  outcomeCounts,
  plainError,
  runStatus,
  scoutRunStatus,
  viaWords,
  when,
  type Tone,
} from "@/lib/integrations/words";
import { errorText } from "@/spa/useApi";

import { useKeyed, type Keyed } from "../hooks";

export function RunLog({ code, scoutRunId, timeZone }: { code: string; scoutRunId: string; timeZone: string }) {
  const log = useKeyed(`log|${code}|${scoutRunId}`, () => getRunLog(code, scoutRunId));
  if (!log.data) {
    return log.error ? <Banner tone="danger" actions={<Button onClick={log.reload}>Try again</Button>}>{log.error}</Banner> : <SkeletonRows rows={4} columns={3} label="Reading Scout’s log" />;
  }
  if (log.data.error) {
    return (
      <Banner tone="warning" title="Scout is not answering, so there is no log." actions={<Button onClick={log.reload}>Try again</Button>}>
        {log.data.error}
      </Banner>
    );
  }
  if (log.data.log.length === 0) return <p style={{ margin: 0, color: "var(--ink-3)" }}>Scout wrote no log for this run.</p>;
  return (
    <ol
      style={{ margin: 0, padding: "var(--s2) var(--s3)", listStyle: "none", display: "grid", gap: 4, maxHeight: "28rem", overflow: "auto", border: "1px solid var(--line)", borderRadius: "var(--r)", background: "var(--surface-2)", fontSize: "var(--t-sm)" }}
      aria-label="Scout’s log"
    >
      {log.data.log.map((line) => (
        <li key={line.id} style={{ display: "grid", gridTemplateColumns: "5.5rem 1.2rem minmax(0, 1fr)", gap: "var(--s2)", alignItems: "baseline" }}>
          <span style={{ color: "var(--ink-3)", fontVariantNumeric: "tabular-nums" }}>
            {new Date(line.at).toLocaleTimeString("en-GB", { timeZone })}
          </span>
          <span style={{ color: line.level === "error" ? "var(--bad)" : line.level === "warning" ? "var(--warn)" : "var(--ink-3)" }}>
            {line.level === "error" ? <CircleAlert size={13} aria-label="error" /> : line.level === "warning" ? <TriangleAlert size={13} aria-label="warning" /> : <Info size={13} aria-label="note" />}
          </span>
          <span style={{ overflowWrap: "anywhere" }}>{line.say}</span>
        </li>
      ))}
    </ol>
  );
}

/** The icon and tone of one entry: what a person should take from it at a glance. */
function look(entry: TimelineEntry): { tone: Tone; icon: ReactNode } {
  if (entry.read === "not_read") return { tone: "warning", icon: <TriangleAlert size={15} aria-label="Passed, not read" /> };
  if (entry.read === "older") return { tone: "neutral", icon: <Minus size={15} aria-label="Older, not read" /> };
  if (entry.read === "nothing") {
    return entry.scout && scoutRunStatus(entry.scout.status).tone === "danger"
      ? { tone: "danger", icon: <X size={15} aria-label="Scout’s run did not pass" /> }
      : { tone: "info", icon: <LoaderCircle size={15} aria-label="Scout is still working" /> };
  }
  const real = entry.ours.filter((run) => run.via !== "try");
  const newest = real[0] ?? entry.ours[0];
  if (!newest) return { tone: "neutral", icon: <Minus size={15} aria-hidden /> };
  const status = runStatus(newest.status);
  if (status.tone === "danger") return { tone: "danger", icon: <X size={15} aria-label={status.word} /> };
  if (newest.via === "try") return { tone: "info", icon: <FlaskConical size={15} aria-label="Tried" /> };
  if (status.tone === "good") return { tone: "good", icon: <Check size={15} aria-label={status.word} /> };
  return { tone: "neutral", icon: <Minus size={15} aria-label={status.word} /> };
}

function OursCell({ entry, base }: { entry: TimelineEntry; base: string }) {
  if (entry.ours.length === 0) {
    if (entry.read === "not_read") {
      return (
        <>
          <TimelineMain>Scout passed, but omnium did not read it.</TimelineMain>
          <TimelineSub>It is newer than anything omnium holds.</TimelineSub>
        </>
      );
    }
    if (entry.read === "older") return <TimelineSub>Not read. omnium already holds a newer run.</TimelineSub>;
    return (
      <TimelineSub>
        {entry.scout && scoutRunStatus(entry.scout.status).tone === "danger" ? "Nothing to read." : "Waiting for Scout to finish."}
      </TimelineSub>
    );
  }
  return (
    <TimelineStack>
      {entry.ours.map((run) => {
        const status = runStatus(run.status);
        const parts = outcomeCounts(run.counts);
        const extra = extraCounts(run.counts);
        return (
          <TimelineOne key={run.id}>
            <TimelineLine>
              <TonePill tone={status.tone} title={run.message ?? undefined}>
                {status.word}
              </TonePill>
              <span>
                {viaWords(run.via)}
                {run.actor ? ` by ${run.actor}` : ""}
                {run.version !== null ? ` · version ${run.version}` : ""}
              </span>
              <Link to={`${base}/${run.id}`}>Open</Link>
            </TimelineLine>
            {parts.length > 0 && <OutcomeBar counts={run.counts} small legend={false} />}
            <TimelineSub>
              {parts.length ? parts.map((part) => `${part.word} ${part.count.toLocaleString()}`).join(" · ") : "No rows"}
              {extra.length ? ` · ${extra.join(" · ")}` : ""}
            </TimelineSub>
          </TimelineOne>
        );
      })}
    </TimelineStack>
  );
}

export function RunsTab({ item, now, runs }: { item: IntegrationDetail; now: number; runs: Keyed<Run[]> }) {
  const timeZone = item.eventTimezone ?? "UTC";
  const [params] = useSearchParams();
  const asked = params.get("scoutRun");
  const scout = useKeyed(`scout-runs|${item.code}`, () => listScoutRuns(item.code, 20));
  const [open, setOpen] = useState<string | null>(asked);
  const base = `/integrations/${encodeURIComponent(item.code)}/runs`;
  const ours = runs.data ?? [];
  const scoutRuns = scout.data?.runs ?? [];
  const entries = lineUpRuns(ours, scoutRuns);
  const askedRow = asked ? scoutRuns.find((run) => run.id === asked) : undefined;
  const askedOurs = asked ? ours.find((run) => run.scoutRunId === asked && run.via !== "try") : undefined;
  const settled = runs.data !== undefined && (scout.data !== undefined || scout.error !== undefined);

  const read = entries.filter((entry) => entry.read === "read").length;
  const notRead = entries.filter((entry) => entry.read === "not_read").length;
  const passed = scoutRuns.filter((run) => run.status.toLowerCase() === "passed").length;
  const failed = scoutRuns.filter((run) => scoutRunStatus(run.status).tone === "danger").length;
  const tries = ours.filter((run) => run.via === "try").length;

  return (
    <Column>
      {asked && (
        <Panel title="The run you opened" note={`Scout run ${asked}.`}>
          {askedOurs ? (
            <p style={{ margin: 0 }}>
              omnium read it: <Link to={`${base}/${askedOurs.id}`}>{runStatus(askedOurs.status).word}, {when(askedOurs.createdAt, timeZone)}</Link>
            </p>
          ) : (
            <Note>omnium has not read this run.</Note>
          )}
          {!askedRow && <RunLog code={item.code} scoutRunId={asked} timeZone={timeZone} />}
        </Panel>
      )}

      {runs.error && <Banner tone="danger" actions={<Button onClick={runs.reload}>Try again</Button>}>{runs.error}</Banner>}
      {scout.error && <Banner tone="danger" actions={<Button onClick={scout.reload}>Try again</Button>}>{scout.error}</Banner>}
      {scout.data?.trouble && (
        <Banner tone="warning" title="Scout is not answering." actions={<Button onClick={scout.reload}>Try again</Button>}>
          {scout.data.trouble} Only the runs omnium holds are shown.
        </Banner>
      )}

      {!settled ? (
        runs.error ? null : <SkeletonRows rows={5} columns={5} label="Reading the runs" />
      ) : entries.length === 0 ? (
        <Card>
          <Empty title="No runs yet" note="A run shows here once Scout runs the job, omnium reads a run, or a person tries a version." />
        </Card>
      ) : (
        <>
          <Tiles>
            <Tile
              label="Read by omnium"
              icon={<Check size={14} aria-hidden />}
              tone={read ? "good" : "neutral"}
              value={read}
              note={scoutRuns.length ? `Of ${passed} passed in Scout’s last ${scoutRuns.length} runs` : "Scout’s runs are not shown"}
            />
            <Tile
              label="Passed, not read"
              icon={<TriangleAlert size={14} aria-hidden />}
              tone={notRead ? "warning" : "neutral"}
              value={notRead}
              note={notRead ? "Newer than anything omnium holds" : "Every new passed run was read"}
            />
            <Tile
              label="Failed in Scout"
              icon={<X size={14} aria-hidden />}
              tone={failed ? "danger" : "neutral"}
              value={failed}
              note="Never reached omnium"
            />
            <Tile label="Tries" icon={<FlaskConical size={14} aria-hidden />} value={tries} note="Tried a version; nothing written" />
          </Tiles>

          <Timeline label="Runs, newest first">
            {entries.map((entry) => {
              const shown = look(entry);
              const said = entry.scout ? scoutRunStatus(entry.scout.status) : null;
              const scoutId = entry.scout?.id ?? null;
              return (
                <TimelineItem
                  key={entry.key}
                  icon={shown.icon}
                  tone={shown.tone}
                  flagged={entry.read === "not_read"}
                  cells={[
                    {
                      key: "when",
                      content: (
                        <>
                          <TimelineMain>{when(entry.at, timeZone)}</TimelineMain>
                          <TimelineSub>
                            {ago(entry.at, now)}
                            {entry.scout?.startedBy ? ` · started by ${entry.scout.startedBy}` : ""}
                          </TimelineSub>
                        </>
                      ),
                    },
                    {
                      key: "scout",
                      label: "Scout",
                      content:
                        entry.scout && said ? (
                          <>
                            <TimelineLine>
                              <TonePill tone={said.tone}>{said.word}</TonePill>
                              <span>{entry.scout.rows.toLocaleString()} rows</span>
                            </TimelineLine>
                            {entry.scout.error && <TimelineSub>{plainError(entry.scout.error)}</TimelineSub>}
                          </>
                        ) : (
                          <TimelineSub>Not in Scout’s recent runs</TimelineSub>
                        ),
                    },
                    { key: "ours", label: "omnium", content: <OursCell entry={entry} base={base} /> },
                  ]}
                  actions={
                    scoutId ? (
                      <Button tone="subtle" aria-expanded={open === scoutId} onClick={() => setOpen(open === scoutId ? null : scoutId)}>
                        {open === scoutId ? <ChevronDown size={14} aria-hidden /> : <ChevronRight size={14} aria-hidden />}
                        {open === scoutId ? "Hide log" : "Scout’s log"}
                      </Button>
                    ) : null
                  }
                  detail={scoutId && open === scoutId ? <RunLog code={item.code} scoutRunId={scoutId} timeZone={timeZone} /> : null}
                />
              );
            })}
          </Timeline>
        </>
      )}
    </Column>
  );
}

export function RunView({ item, runId, canWrite }: { item: IntegrationDetail; runId: string; canWrite: boolean }) {
  const timeZone = item.eventTimezone ?? "UTC";
  const detail = useKeyed(`run|${item.code}|${runId}`, () => getRun(item.code, runId, { limit: 1 }));
  const versions = useKeyed(`versions|${item.code}`, () => listVersions(item.code));
  const [outcome, setOutcome] = useState<string | null>(null);
  const [version, setVersion] = useState<number | null>(null);
  const [asking, setAsking] = useState(false);
  const [busy, setBusy] = useState(false);
  const [said, setSaid] = useState<{ tone: "good" | "danger"; text: string } | null>(null);
  const back = `/integrations/${encodeURIComponent(item.code)}/runs`;

  if (!detail.data) {
    return detail.error ? (
      <Banner tone="danger" actions={<Link to={back}>Back to runs</Link>}>
        {detail.error}
      </Banner>
    ) : (
      <SkeletonRows rows={6} columns={4} />
    );
  }
  const run = detail.data.run;
  const status = runStatus(run.status);
  const usable = versions.data ?? [];
  const chosen = version ?? item.liveVersion ?? usable[0]?.number ?? null;

  const replay = () => {
    setBusy(true);
    replayRun(item.code, { run: run.id, version: chosen })
      .then(
        () => setSaid({ tone: "good", text: "Queued. The scheduler sends this run again; a new run shows under Runs when it is done." }),
        (thrown) => setSaid({ tone: "danger", text: errorText(thrown, "omnium did not queue the replay") }),
      )
      .finally(() => {
        setBusy(false);
        setAsking(false);
      });
  };

  return (
    <Column gap="lg">
      <Crumbs trail={[{ label: "Runs", href: back }, { label: `Run of ${when(run.createdAt, timeZone)}` }]} />
      <Section title={`Run of ${when(run.createdAt, timeZone)}`}>
        <Card>
          <div style={{ display: "grid", gap: "var(--s3)" }}>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "var(--s2) var(--s4)", alignItems: "center" }}>
              <TonePill tone={status.tone}>{status.word}</TonePill>
              <span>{viaWords(run.via)}{run.actor ? ` · by ${run.actor}` : ""}</span>
              <span>Version {run.version ?? "—"}</span>
              <span>{run.rowsCollected.toLocaleString()} rows from Scout</span>
              {run.scoutStartedAt && <span style={{ color: "var(--ink-3)" }}>Scout started it {when(run.scoutStartedAt, timeZone)}</span>}
            </div>
            <OutcomeBar counts={run.counts} onPick={setOutcome} />
            {extraCounts(run.counts).length > 0 && (
              <span style={{ color: "var(--ink-3)", fontSize: "var(--t-sm)" }}>Records: {extraCounts(run.counts).join(" · ")}</span>
            )}
            {run.message && <p style={{ margin: 0, whiteSpace: "pre-wrap", overflowWrap: "anywhere", color: "var(--ink-2)" }}>{run.message}</p>}
          </div>
        </Card>
      </Section>

      <Section title="Replay" note="Send this run again through a version — to fix old data after fixing a mapping. Scout is not called.">
        <Column gap="sm">
          {said && <Banner tone={said.tone}>{said.text}</Banner>}
          <FieldRow>
            <Field label="With version">
              <Select value={chosen ?? ""} onChange={(event) => setVersion(Number(event.target.value))} disabled={!canWrite || busy}>
                {usable.map((one) => (
                  <option key={one.number} value={one.number}>
                    Version {one.number} ({one.status})
                  </option>
                ))}
              </Select>
            </Field>
            <Field label=" ">
              <div>
                <Button disabled={!canWrite || busy || chosen === null} onClick={() => setAsking(true)}>
                  Replay with version {chosen ?? "—"}
                </Button>
              </div>
            </Field>
          </FieldRow>
          {asking && (
            <Banner
              tone="warning"
              actions={
                <>
                  <Button tone="primary" disabled={busy} onClick={replay}>
                    {busy ? "Queueing…" : "Yes, send it again"}
                  </Button>
                  <Button tone="subtle" disabled={busy} onClick={() => setAsking(false)}>
                    Cancel
                  </Button>
                </>
              }
            >
              This writes into {item.eventName} now, through version {chosen}. Guards do not stop a replay.
            </Banner>
          )}
        </Column>
      </Section>

      <Section title="Rows" note="What happened to each row, and why. Open a row to see what Scout sent beside what omnium made of it.">
        <RunRows code={item.code} runId={run.id} counts={run.counts} outcome={outcome} onOutcome={setOutcome} />
      </Section>

      {run.scoutRunId && (
        <Section title="Scout’s log" note="Every page Scout opened, every retry and check, in order.">
          <RunLog code={item.code} scoutRunId={run.scoutRunId} timeZone={timeZone} />
        </Section>
      )}
    </Column>
  );
}
